/**
 * QuestManager - Tracks objectives and level completion for dungeon mode.
 */
export default class QuestManager {
  constructor(options = {}) {
    this.hudManager = options.hudManager || null;
    this.world = options.world || null;
    this.onAllComplete = options.onAllComplete || null;

    /** @type {Array<{type: string, description: string, completed: boolean, target?: string, count?: number, itemId?: string}>} */
    this.objectives = [];

    this._killCounts = new Map();
    this._totalKills = 0;
  }

  /**
   * Set up objectives for a level.
   * @param {Array<Object>} objectives
   */
  setObjectives(objectives) {
    this.objectives = objectives.map(o => ({
      ...o,
      completed: false,
      // Normalize: support both 'label' and 'description'
      description: o.description || o.label || ''
    }));
    this._killCounts.clear();
    this._totalKills = 0;
    this._updateHUD();
  }

  /**
   * Called when an enemy is killed.
   */
  onEnemyKilled(enemy) {
    this._totalKills++;
    const type = enemy.characterName || 'unknown';
    this._killCounts.set(type, (this._killCounts.get(type) || 0) + 1);

    for (const obj of this.objectives) {
      if (obj.completed) continue;

      if (obj.type === 'kill_all') {
        // Check if all hostile NPCs are dead
        if (this.world) {
          let anyAlive = false;
          for (const char of this.world.characters.values()) {
            if (char.isNPC && char.combatStats && !char.combatStats.isDead && char.combatStats.isHostile) {
              anyAlive = true;
              break;
            }
          }
          if (!anyAlive) obj.completed = true;
        }
      } else if (obj.type === 'kill_count') {
        const count = obj.target
          ? (this._killCounts.get(obj.target) || 0)
          : this._totalKills;
        if (count >= (obj.count || 1)) obj.completed = true;
      } else if (obj.type === 'kill_boss') {
        if (enemy.combatStats?.isBoss) obj.completed = true;
      }
    }

    this._updateHUD();
    this._checkCompletion();
  }

  /**
   * Called when player picks up a quest item.
   */
  onItemAcquired(itemId) {
    for (const obj of this.objectives) {
      if (obj.completed) continue;
      if (obj.type === 'find_item' && obj.itemId === itemId) {
        obj.completed = true;
      }
    }
    this._updateHUD();
    this._checkCompletion();
  }

  /**
   * Called when player reaches an exit zone.
   */
  onExitReached() {
    for (const obj of this.objectives) {
      if (obj.completed) continue;
      if (obj.type === 'reach_exit') obj.completed = true;
    }
    this._updateHUD();
    this._checkCompletion();
  }

  _checkCompletion() {
    if (this.objectives.length === 0) return;
    const allComplete = this.objectives.every(o => o.completed);
    if (allComplete && this.onAllComplete) {
      window.game?.audioSystem?.audio?.playUiFanfare();
      this.onAllComplete();
    }
  }

  _updateHUD() {
    if (this.hudManager) {
      this.hudManager.updateObjectives(this.objectives);
    }
    if (this.onObjectivesChanged) {
      this.onObjectivesChanged(this.objectives);
    }
  }

  /** Whether all objectives are complete */
  get allComplete() {
    return this.objectives.length > 0 && this.objectives.every(o => o.completed);
  }

  /** Reset all state */
  reset() {
    this.objectives = [];
    this._killCounts.clear();
    this._totalKills = 0;
    this._updateHUD();
  }
}
